/**
 * The traffic model id service implementation. Provides current traffic model id which is the traffic model number
 * needed for the Online Traffic Incidents Layer, and Online Traffic Incident Details API calls. It is updated every
 * minute, and is valid for two minutes before it times out.
 *
 * The tomtom.trafficModelId() method creates a new instance of service. Parameter can be passed on creation or
 * provided via convenient function that can be chained. The method go() performs the actual call.
 *
 * The call is asynchronous therefore user have two options to handle response, either by passing callback function or
 * by using [promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise). The
 * SDK provides polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * @class trafficModelId
 */
define('services/trafficModelId',["rest/trafficViewport", "model/traffic/trafficModelId", "./chain", "./validators"],
    function (trafficViewport, model, chain, validators) {
        "use strict";

        var fields = {
            /**
             * A setter and getter for _'key'_ attribute. Key can be set globally when initializing map, with
             * tomtom.key or tomtom.trafficModelId.key function. But also per service instance on initialization or
             * with this method.
             *
             * The API Key can be issued from http://developer.tomtom.com
             *
             * @method key
             * @chainable
             * @param {String} key API Key valid for requested service.
             * @throws {TypeError} if the given argument cannot be converted to a valid value
             * @return {trafficModelId|String} Method returns the service instance allowing chaining or key value if
             *     argument is omitted.
             */
            key: {validator: validators.string},
            /**
             * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
             *
             * @method protocol
             * @chainable
             * @param {String} [protocol] Protocol type, one of _http_ or _https_.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {trafficModelId|String} Method returns the service instance allowing chaining or _protocol_
             * value if argument is omitted.
             */
            protocol: {validator: validators.oneOfValue(["http", "https"], "protocol")},
            baseUrl: {validator: validators.string},
            serviceUrl: {validator: validators.string}
        };

        /**
         * The function performing actual call to the web service.
         *
         * Callback function takes a response parameter and fail callback takes error as only parameter.
         *
         * The final response from service is a traffic model number needed for the Online Traffic Incidents Layer, and
         * Online Traffic Incident Details API calls. It is updated every minute, and is valid for two minutes before
         * it times out.
         *
         * @method go
         * @param {Function} [callback] The callback function to be called on success.
         * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
         *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
         * @return {Promise} If callback is omitted function returns promise
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * // this is optional if you passed global key earlier
         * tomtom.trafficModelId.key(<Your API key>);
         * var trafficModelId = tomtom.trafficModelId().callback(myFunction).go();
         * ```
         */
        /**
         * A setter and getter for callback function. This function is called after receiving successful response from
         * service. It takes one argument which is the response HTML from service.
         *
         * @method callback
         * @chainable
         * @param {Function} [callback] The callback function to be called on success.
         * @throws {TypeError} if the given argument is not a function
         * @return {trafficModelId|Function} Method returns the service instance allowing chaining or function if
         *     argument is omitted.
         */
        /**
         * A setter and getter for the fail callback function. This function is called after an unsuccessful response
         * from the service or after a communication error. It takes one argument which is the error description.
         *
         * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
         * handle the response. When using promises, errors are handled with the second attribute of the then() method
         * or with the catch() method.
         *
         * By default the fail function is:
         * ```javascript
         *
         * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
         * ```
         * @method fail
         * @chainable
         * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
         *     the console and thrown.
         * @throws {TypeError} If the given argument is not a function.
         * @return {trafficModelId|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */
        function handleChain(data, callback) {
            trafficViewport.trafficViewport(data, function (response) {
                callback.call(this, model(response));
            });
        }

        /**
         * The method to instantiates the service. The options can be passed on creation or later during the execution
         * using function with the name matching parameter name. Each option can be retrieved using the exactly same
         * function but without parameter. The instance of service can be reused by calling multiple times go()
         * function.
         *
         * @method trafficModelId
         * @param {Object} [options] Options to be passed to the trafficModelIdCaption call.
         * @param {String} [options.key] This is optional if key was already passed.
         * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
         * @return {trafficModelId} Method returns new object that allows communication with the service.
         *
         * @example
         * ```javascript
         *
         * tomtom.trafficModelId({key: "<Your API key>"}).go().then(function(response) {
         *   console.log(response);
         * });
         * ```
         */
        return function (options) {
            return chain(fields, handleChain, options);
        };
    }
);

